import React, { useState, useEffect, useContext, useRef } from "react";
import { Modal, Button, Form, Table, Row, Col, Spinner } from "react-bootstrap";
import { AuthContext } from "../../contexts/AuthContext";
import {
  getComparisonSheetData,
  extendDueDate,
  addCustomerToEnquiry,
  freezeEnquiry,
  rejectEnquiry,
  withdrawBidder,
  updateCustomerRemarks
} from "../../services/finalize-bid-service";
import { addEnquiryTender, addEnquiryTenderMail } from "../../services/enquiry-tender-service";
import { getCustomerUsersByCode } from "../../services/user-service";
import { formatDate, formatDateInDDMMYYYY, formatDateYYYY_MM_DD } from "../../functions/common-functions";
import { addLogDetails } from "../../services/logger-entry-service";
import MultiValuePopUp from "../utils/MultiValuePopUp";
import GridMaster from "../masters/GridMaster";
import ComparisonSheetPDFModal from "./ComparisonSheetPDFModal";
import ReviseDueDateModal from "./ReviseDueDateModal";
import BidHistoryModal from "./BidHistoryModal";
import WithdrawBidderModal from "./WithdrawBidderModal";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import successImage from "../../icons/success-tick-logo.svg";
import errorImage from "../../icons/error-tick-logo.svg";
import download from "../../icons/PDFDownload.svg";
import "./FinalizeBid.css";
import { ALERT_PARAMS } from "../../utils/constant.js";

const ComparisonSheet = ({ show, onHide, enquiry, onUpdate, setReload, reload, comparisonData }) => {
  const MySwal = withReactContent(Swal);
  const context = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [enquiryData, setEnquiryData] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selectedForFinalize, setSelectedForFinalize] = useState({});
  const [selectedForDueDate, setSelectedForDueDate] = useState({});
  const [customerRemarks, setCustomerRemarks] = useState({});
  const [enquiryRemarks, setEnquiryRemarks] = useState("");
  // const [lnCompany, setLnCompany] = useState("");
  const [showBidHistory, setShowBidHistory] = useState(false);
  const [selectedCustomerHistory, setSelectedCustomerHistory] = useState(null);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState("");
  const [selectedWithdrawCustomer, setSelectedWithdrawCustomer] = useState(null);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [showReviseModal, setShowReviseModal] = useState(false);
  const [revisedDueDate, setRevisedDueDate] = useState("");
  const [comparisonColumns, setComparisonColumns] = useState([]);
  const [historyColumns, setHistoryColumns] = useState([]);
  const [comparisonRowData, setComparisonRowData] = useState([]);
  const [historyRowData, setHistoryRowData] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (show && enquiry) {
      console.log('ComparisonSheet opened for enquiry:', enquiry);
      console.log('Received comparison data:', comparisonData);

      if (comparisonData) {
        // Use data passed from parent
        setEnquiryData(comparisonData.enquiryData);
        setCustomers(comparisonData.customers || []);
        setComparisonRowData(comparisonData.customers || []);

        // Initialize customer remarks
        const remarksData = {};
        (comparisonData.customers || []).forEach(customer => {
          remarksData[customer.CustomerCode] = customer.CustomerRemarks || "";
        });
        setCustomerRemarks(remarksData);

        // Initialize selections
        initializeSelections(comparisonData.customers || []);
      } else {
        // Fallback to API call if no data provided
        fetchComparisonData();
      }
    }
  }, [show, enquiry, comparisonData]);

  // Setup grid columns after customers data is available
  useEffect(() => {
    if (customers.length > 0) {
      console.log('Setting up grid columns for', customers.length, 'customers', customers);
      setupGridColumns();
    }
  }, [customers]);

  // Separate effect for selection changes to avoid infinite loops
  useEffect(() => {
    if (customers.length > 0 && comparisonColumns.length > 0) {
      console.log('Updating selections for existing columns');
      // Re-setup columns when selections change
      setupGridColumns();
    }
  }, [selectedForFinalize, selectedForDueDate]);

  // Force re-render when columns are set
  useEffect(() => {
    console.log('Comparison columns updated:', comparisonColumns.length, 'columns');
  }, [comparisonColumns]);

  const fetchComparisonData = async () => {
    setLoading(true);
    try {
      console.log('Fetching comparison data for enquiry:', enquiry.EnquiryNumber);
      const data = await getComparisonSheetData({ EnquiryNumber: enquiry.EnquiryNumber });
      console.log('Comparison data received:', data);

      if (!data || !data.enquiryData) {
        console.error('No enquiry data received from API');
        setEnquiryData(null);
        setCustomers([]);
        setComparisonRowData([]);
        setLoading(false);
        return;
      }

      setEnquiryData(data.enquiryData);

      // Sort customers by bid amount (highest first), handling different data types
      const sortedCustomers = [...(data.customers || [])].sort((a, b) => {
        const bidA = a.CurrentBid === "Regret" || a.CurrentBid === "No Bid Placed" ? -1 : parseFloat(a.CurrentBid) || 0;
        const bidB = b.CurrentBid === "Regret" || b.CurrentBid === "No Bid Placed" ? -1 : parseFloat(b.CurrentBid) || 0;

        // If both are invalid bids, maintain original order
        if (bidA === -1 && bidB === -1) return 0;
        // If only A is invalid, B comes first
        if (bidA === -1) return 1;
        // If only B is invalid, A comes first
        if (bidB === -1) return -1;
        // Both are valid numbers, sort descending
        return bidB - bidA;
      });

      console.log('Sorted customers:', sortedCustomers);
      setCustomers(sortedCustomers);
      setComparisonRowData(sortedCustomers);

      // Initialize customer remarks from existing data
      const remarksData = {};
      sortedCustomers.forEach(customer => {
        remarksData[customer.CustomerCode] = customer.CustomerRemarks || "";
      });
      setCustomerRemarks(remarksData);

      // Initialize selections based on business logic
      initializeSelections(sortedCustomers);
    } catch (error) {
      console.error("Error fetching comparison data:", error);
      setEnquiryData(null);
      setCustomers([]);
      setComparisonRowData([]);
    }
    setLoading(false);
  };

  const setupGridColumns = () => {
    console.log('setupGridColumns called with customers:', customers.length);

    // Comparison Sheet Grid Columns
    const compCols = [
      {
        headerName: "Select for Finalize",
        field: "selectFinalize",
        cellRenderer: (params) => {
          // Use CustomerCode as unique identifier
          const customerId = params.data.CustomerCode;
          const checkboxState = selectedForFinalize[customerId] || { checked: false, disabled: true };
          return (
            <Form.Check
              type="checkbox"
              checked={checkboxState.checked}
              disabled={checkboxState.disabled}
              onChange={(e) => {
                setSelectedForFinalize(prev => ({
                  ...prev,
                  [customerId]: {
                    checked: e.target.checked,
                    disabled: checkboxState.disabled
                  }
                }));
              }}
            />
          );
        },
        minWidth: 120,
        filter: false,
        sortable: false
      },
      {
        headerName: "Select for Due Date Extension",
        field: "selectDueDate",
        cellRenderer: (params) => {
          // Use CustomerCode as unique identifier
          const customerId = params.data.CustomerCode;
          const checkboxState = selectedForDueDate[customerId] || { checked: false, disabled: false };
          // Disable checkbox if customer is withdrawn
          const isDisabled = checkboxState.disabled || params.data.WithdrawStatus === "Withdrawn";
          return (
            <Form.Check
              type="checkbox"
              checked={checkboxState.checked}
              disabled={isDisabled}
              onChange={(e) => {
                setSelectedForDueDate(prev => ({
                  ...prev,
                  [customerId]: {
                    checked: e.target.checked,
                    disabled: checkboxState.disabled
                  }
                }));
              }}
            />
          );
        },
        minWidth: 120,
        filter: false,
        sortable: false
      },
      { headerName: "BP Code", field: "CustomerCode", filter: true, minWidth: 200 },
      { headerName: "BP Name", field: "CustomerName", filter: true, minWidth: 150 },
      {
        headerName: "Price ₹ (Excl. taxes)",
        field: "CurrentBid",
        filter: true,
        minWidth: 130,
        valueFormatter: (params) => params.value || "No Bid Placed"
      },
      {
        headerName: "Bid Submission Date Time",
        field: "CurrentBidDate",
        filter: true,
        minWidth: 180,
        valueFormatter: (params) => formatBidDateTime(params.value)
      },
      {
        headerName: "Revised Due Date",
        field: "RevisedDueDate",
        filter: true,
        minWidth: 160,
        valueFormatter: (params) => params.value || "-"
      },
      {
        headerName: "History",
        field: "history",
        cellRenderer: (params) => (
          <Button
            variant="outline-primary"
            className="swal2-styled.swal2-deny"
            size="sm"
            onClick={() => handleShowHistory(params.data)}
          >
            History
          </Button>
        ),
        minWidth: 120,
        filter: false,
        sortable: false
      },
      {
        headerName: "Action",
        field: "action",
        cellRenderer: (params) => {
          const highestBidder = getHighestBidder();
          const customerId = params.data.CustomerCode;
          const highestBidderId = highestBidder ? highestBidder.CustomerCode : null;
          const isHighestBidder = highestBidderId && customerId === highestBidderId;
          const hasValidBid = params.data.CurrentBid &&
            params.data.CurrentBid !== "Regret" &&
            params.data.CurrentBid !== "No Bid Placed" &&
            !isNaN(parseFloat(params.data.CurrentBid));

          if (isHighestBidder && hasValidBid && params.data.WithdrawStatus !== "Withdrawn") {
            return (
              <Button
                variant="outline-primary"
                className="swal2-styled.swal2-deny"
                size="sm"
                onClick={() => {
                  setSelectedWithdrawCustomer(params.data);
                  setShowWithdrawModal(true);
                  setWithdrawReason("");
                }}
              >
                Withdraw
              </Button>
            );
          }

          // Show withdrawn status if customer has withdrawn
          if (params.data.WithdrawStatus === "Withdrawn") {
            return (
              <span className="text-danger">
                Withdrawn: {params.data.WithdrawReason}
              </span>
            );
          }

          return null;
        },
        minWidth: 150,
        filter: false,
        sortable: false
      }
    ];

    // History Grid Columns
    const histCols = [
      { headerName: "BP Code", field: "BPCode", minWidth: 200, filter: true, tooltipField: "BPCode" },
      { headerName: "BP Name", field: "BPName", minWidth: 200, filter: true, tooltipField: "BPName" },
      { headerName: "User ID/Name", field: "UserIdName", minWidth: 100, filter: true, tooltipField: "UserIdName" },
      { headerName: "Email ID", field: "EmailId", minWidth: 150, filter: true, tooltipField: "EmailId" },
      { headerName: "Bid Submission Date Time", field: "BidSubmissionDateTime", minWidth: 150, filter: true, tooltipField: "BidSubmissionDateTime" },
      { headerName: "Price (₹)", field: "Price", minWidth: 100, filter: true, tooltipField: "Price" }
    ];

    console.log('Setting comparison columns:', compCols.length, 'columns');
    console.log('Setting history columns:', histCols.length, 'columns');

    setComparisonColumns([...compCols]); // Force new array reference
    setHistoryColumns([...histCols]); // Force new array reference
  };

  const initializeSelections = (sortedCustomers) => {
    const finalizeSelections = {};
    const dueDateSelections = {};

    if (sortedCustomers.length > 0) {
      // Get valid bids (numeric values, not "Regret" or "No Bid Placed")
      const validBids = sortedCustomers.filter(c => {
        const bid = parseFloat(c.CurrentBid);
        return !isNaN(bid) && c.CurrentBid !== "Regret" && c.CurrentBid !== "No Bid Placed" && c.WithdrawStatus !== "Withdrawn";
      });

      if (validBids.length === 0) {
        // Scenario 1: All customers have "Regret" or "No Bid Placed"
        sortedCustomers.forEach(customer => {
          const customerId = customer.CustomerCode;
          finalizeSelections[customerId] = {
            checked: false,
            disabled: true // Disabled for finalize
          };
          dueDateSelections[customerId] = {
            checked: false,
            disabled: customer.WithdrawStatus === "Withdrawn" // Disabled if withdrawn
          };
        });
      } else {
        // Get highest bid value from valid bids
        const highestBid = Math.max(...validBids.map(c => parseFloat(c.CurrentBid)));
        const highestBidders = validBids.filter(c => parseFloat(c.CurrentBid) === highestBid);

        if (highestBidders.length === 1) {
          // Scenario 2: Unique highest bid - auto-select and disable
          const highestBidder = highestBidders[0];
          const highestBidderId = highestBidder.CustomerCode;

          sortedCustomers.forEach(customer => {
            const customerId = customer.CustomerCode;
            const isHighestBidder = customerId === highestBidderId;

            if (isHighestBidder && customer.WithdrawStatus !== "Withdrawn") {
              // Highest bidder: checked and disabled
              finalizeSelections[customerId] = {
                checked: true,
                disabled: true
              };
            } else {
              // All others: unchecked and disabled
              finalizeSelections[customerId] = {
                checked: false,
                disabled: true
              };
            }

            dueDateSelections[customerId] = {
              checked: false,
              disabled: customer.WithdrawStatus === "Withdrawn"
            };
          });
        } else {
          // Scenario 3: Multiple customers with same highest bid - enable for selection
          sortedCustomers.forEach(customer => {
            const customerId = customer.CustomerCode;
            const customerBid = parseFloat(customer.CurrentBid);
            const isHighestBidder = customerBid === highestBid;

            if (isHighestBidder && customer.WithdrawStatus !== "Withdrawn") {
              // Highest bidders: unchecked but enabled
              finalizeSelections[customerId] = {
                checked: false,
                disabled: false
              };
            } else {
              // All others: unchecked and disabled
              finalizeSelections[customerId] = {
                checked: false,
                disabled: true
              };
            }

            dueDateSelections[customerId] = {
              checked: false,
              disabled: customer.WithdrawStatus === "Withdrawn"
            };
          });
        }
      }
    }
    setSelectedForFinalize(finalizeSelections);
    setSelectedForDueDate(dueDateSelections);
  };

  const formatBidDateTime = (input, { useUTC = false, strict = true } = {}) => {
    if (input == null || input === "") return "-";

    // Helper: parse "DD/MM/YYYY HH:mm"
    const parseDDMMYYYY_HHmm = (str) => {
      if (typeof str !== "string") return null;

      const [datePart, timePart = ""] = str.trim().split(/\s+/);
      if (!datePart) return null;

      const [ddStr, mmStr, yyyyStr] = datePart.split("/");
      if (!ddStr || !mmStr || !yyyyStr) return null;

      const dd = Number(ddStr);
      const mm = Number(mmStr);
      const yyyy = Number(yyyyStr);

      let HH = 0, MM = 0;
      if (timePart) {
        const [HHStr, MMStr = "0"] = timePart.split(":");
        HH = Number(HHStr);
        MM = Number(MMStr);
      }

      // Basic range checks
      const basicValid =
        Number.isInteger(dd) && Number.isInteger(mm) && Number.isInteger(yyyy) &&
        Number.isInteger(HH) && Number.isInteger(MM) &&
        mm >= 1 && mm <= 12 &&
        dd >= 1 && dd <= 31 &&
        HH >= 0 && HH <= 23 &&
        MM >= 0 && MM <= 59;

      if (!basicValid) return null;

      // Build date (local or UTC)
      const date = useUTC
        ? new Date(Date.UTC(yyyy, mm - 1, dd, HH, MM, 0, 0))
        : new Date(yyyy, mm - 1, dd, HH, MM, 0, 0);

      if (Number.isNaN(date.getTime())) return null;

      // Strict validation: ensure constructed date matches components (e.g., reject 31/02)
      if (strict) {
        const d = useUTC ? date.getUTCDate() : date.getDate();
        const m = (useUTC ? date.getUTCMonth() : date.getMonth()) + 1;
        const y = useUTC ? date.getUTCFullYear() : date.getFullYear();
        const h = useUTC ? date.getUTCHours() : date.getHours();
        const min = useUTC ? date.getUTCMinutes() : date.getMinutes();
        if (d !== dd || m !== mm || y !== yyyy || h !== HH || min !== MM) {
          return null;
        }
      }

      return date;
    };

    // Normalize input to Date
    let dateObj = null;

    if (input instanceof Date) {
      dateObj = input;
    } else if (typeof input === "number") {
      // timestamp in ms
      dateObj = new Date(input);
    } else if (typeof input === "string") {
      // 1) Try DD/MM/YYYY HH:mm
      dateObj = parseDDMMYYYY_HHmm(input);

      // 2) Fallback: native parse (ISO or recognized formats)
      if (!dateObj) {
        const t = Date.parse(input);
        if (!Number.isNaN(t)) dateObj = new Date(t);
      }
    }

    if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return "-";

    // Format using en-IN with capital AM/PM
    const opts = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
      ...(useUTC ? { timeZone: "UTC" } : {}),
    };

    const formatted = dateObj.toLocaleString("en-IN", opts);
    // Convert am/pm to AM/PM
    return formatted.replace(/\b(am|pm)\b/gi, (match) => match.toUpperCase());
  };

  const handleReviseDate = () => {
    const selectedIds = Object.keys(selectedForDueDate).filter(id => selectedForDueDate[id]?.checked);
    if (selectedIds.length === 0) {
      MySwal.fire({
        text: "Please select at least one customer for due date extension",
        imageUrl: errorImage,
        customClass: "swal-error"
      });
      return;
    }
    setShowReviseModal(true);
  };

  const handlePdfDownload = () => {
    setShowPDFModal(true);
  }

  const handleSaveRevisedDate = async () => {
    if (!revisedDueDate) {
      MySwal.fire({
        text: "Please select a revised due date",
        imageUrl: errorImage,
        customClass: "swal-error"
      });
      return;
    }

    const today = formatDateYYYY_MM_DD(new Date());
    if (revisedDueDate < today) {
      MySwal.fire({
        text: "Please select today's date or future date",
        imageUrl: errorImage,
        customClass: "swal-error"
      });
      return;
    }

    const selectedIds = Object.keys(selectedForDueDate).filter(id => selectedForDueDate[id]?.checked);

    // Build array of customer data with both email and code
    const selectedCustomersData = selectedIds.map(id => {
      const customer = customers.find(c => c.CustomerCode === id);
      return {
        CustomerEmail: customer?.CustomerEmail,
        CustomerCode: customer?.CustomerCode
      };
    }).filter(data => data.CustomerEmail && data.CustomerCode);

    try {
      setLoading(true);
      const result = await extendDueDate({
        EnquiryNumber: enquiry.EnquiryNumber,
        CustomersData: selectedCustomersData,
        DueDate: revisedDueDate,
        ModifiedBy: context.userId,
        ModifiedByName: context.userName
      });
      console.log("extendDueDate result", result);
      if (result.status) {
        MySwal.fire({
          text: "Due date revised successfully and mail sent to customers",
          imageUrl: successImage,
          customClass: "swal-success"
        });
        setShowReviseModal(false);
        setRevisedDueDate("");
        fetchComparisonData();
      } else {
        MySwal.fire({
          text: "Unable to Process Request",
          imageUrl: errorImage,
          customClass: "swal-error"
        });
      }
    } catch (error) {
      MySwal.fire({
        text: "Unable to Process Request",
        imageUrl: errorImage,
        customClass: "swal-error"
      });
    } finally {
      setLoading(false);
    }
  };



  const handleFreezeEnquiry = async () => {
    // if (!lnCompany && enquiryData.EnquirySource !== "Disposal") {
    //   MySwal.fire({
    //     text: "Please enter LN Company",
    //     imageUrl: errorImage,
    //     customClass: "swal-error"
    //   });
    //   return;
    // }

    const selectedCustomerIds = Object.keys(selectedForFinalize)
      .filter(id => selectedForFinalize[id].checked);

    // Convert customer IDs back to emails for the API call
    const selectedCustomers = selectedCustomerIds.map(id => {
      const customer = customers.find(c => (c.CustomerCode) === id);
      return customer?.CustomerEmail || id;
    }).filter(email => email);
    console.log("selectedCustomers===>", selectedCustomers);

    // if (selectedCustomers.length === 0) {
    //   MySwal.fire({
    //     text: "Please select customers to finalize",
    //     imageUrl: errorImage,
    //     customClass: "swal-error"
    //   });
    //   return;
    // }

    MySwal.fire({
      title: "Freeze Enquiry",
      text: "Are you sure you want to freeze this enquiry?",
      confirmButtonText: "Freeze",
      cancelButtonText: "Cancel",
      ...ALERT_PARAMS,
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          const result = await freezeEnquiry({
            BidStatus: "Frozen",
            EnquiryStatus: "Frozen",
            EnquiryNumber: enquiry.EnquiryNumber,
            SelectedCustomers: selectedCustomers,
            LogisticCompany: enquiryData.LogisticCompany,
            Remarks: enquiryRemarks,
            ModifiedBy: context.userId,
            ModifiedByName: context.userName
          });

          if (result.status) {
            // Add log entry
            await addLogDetails(
              { EnquiryNumber: enquiry.EnquiryNumber, Action: "Freeze" },
              "FinalizeBid",
              enquiry.EnquiryNumber,
              "FreezeLog",
              { userName: context.userName, userId: context.userId },
              "Transaction"
            );

            MySwal.fire({
              text: "Enquiry frozen successfully",
              imageUrl: successImage,
              customClass: "swal-success"
            });
            if (typeof onUpdate === 'function') {
              onUpdate();
            }
            onHide();
            setReload(!reload);
          } else {
            MySwal.fire({
              text: "Unable to Process Request",
              imageUrl: errorImage,
              customClass: "swal-error"
            });
          }
        } catch (error) {
          console.error("Error freezing enquiry:", error);
          MySwal.fire({
            text: "Unable to Process Request",
            imageUrl: errorImage,
            customClass: "swal-error"
          });
        }
        setLoading(false);
      }
    });
  };

  const handleRejectEnquiry = async () => {
    // if (!lnCompany && enquiryData.EnquirySource !== "Disposal") {
    //   MySwal.fire({
    //     text: "Please enter LN Company",
    //     imageUrl: errorImage,
    //     customClass: "swal-error"
    //   });
    //   return;
    // }

    MySwal.fire({
      title: "Reject Enquiry",
      text: "Are you sure you want to reject this enquiry?",
      confirmButtonText: "Reject",
      cancelButtonText: "Cancel",
      ...ALERT_PARAMS,
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          const result = await rejectEnquiry({
            BidStatus: "Rejected",
            EnquiryStatus: "Rejected",
            EnquiryNumber: enquiry.EnquiryNumber,
            LogisticCompany: enquiryData.LogisticCompany,
            ModifiedBy: context.userId,
            ModifiedByName: context.userName
          });

          if (result.status) {
            // Add log entry
            await addLogDetails(
              { EnquiryNumber: enquiry.EnquiryNumber, Action: "Reject" },
              "FinalizeBid",
              enquiry.EnquiryNumber,
              "RejectLog",
              { userName: context.userName, userId: context.userId },
              "Transaction"
            );

            MySwal.fire({
              text: "Enquiry rejected successfully",
              imageUrl: successImage,
              customClass: "swal-success"
            });
            if (typeof onUpdate === 'function') {
              onUpdate();
            }
            onHide();
          } else {
            MySwal.fire({
              text: "Unable to Process Request",
              imageUrl: errorImage,
              customClass: "swal-error"
            });
          }
        } catch (error) {
          console.error("Error rejecting enquiry:", error);
          MySwal.fire({
            text: "Unable to Process Request",
            imageUrl: errorImage,
            customClass: "swal-error"
          });
        }
        setLoading(false);
      }
    });
  };

  const handleWithdrawBidder = async () => {
    if (!withdrawReason.trim()) {
      MySwal.fire({
        text: "Please enter withdrawal reason",
        imageUrl: errorImage,
        customClass: "swal-error"
      });
      return;
    }

    if (withdrawReason.length > 200) {
      setError("Reason cannot exceed 200 characters");
      return;
    }

    // Validate alphanumeric with allowed special characters
    const allowedPattern = /^[a-zA-Z0-9\s.,()]+$/;
    if (!allowedPattern.test(withdrawReason)) {
      setError("Only alphanumeric characters, space, dot, comma, and brackets are allowed");
      return;
    }


    MySwal.fire({
      text: "Are you sure? This action cannot be undone.",
      confirmButtonText: "Withdraw",
      cancelButtonText: "Cancel",
      ...ALERT_PARAMS
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          const result = await withdrawBidder({
            EnquiryNumber: enquiry.EnquiryNumber,
            CustomerEmail: selectedWithdrawCustomer.CustomerEmail,
            CustomerCode: selectedWithdrawCustomer.CustomerCode,
            WithdrawReason: withdrawReason,
            ModifiedBy: context.userId,
            ModifiedByName: context.userName
          });

          if (result.status) {
            MySwal.fire({
              text: "Bidder withdrawn successfully",
              imageUrl: successImage,
              customClass: "swal-success"
            });
            setShowWithdrawModal(false);
            setWithdrawReason("");
            setSelectedWithdrawCustomer(null);
            // Refresh data to update highest bidder and row highlighting
            await fetchComparisonData();
          } else {
            MySwal.fire({
              text: "Unable to Process Request",
              imageUrl: errorImage,
              customClass: "swal-error"
            });
          }
        } catch (error) {
          console.error("Error withdrawing bidder:", error);
          MySwal.fire({
            text: "Unable to Process Request",
            imageUrl: errorImage,
            customClass: "swal-error"
          });
        }
        setLoading(false);
      }
    });
  };

  const handleAddCustomer = () => {
    setShowAddCustomerModal(true);
  };

  const handleAddSelectedCustomers = async (selectedCustomers) => {
    let customersArray = [];
    if (selectedCustomers?.result && Array.isArray(selectedCustomers.result)) {
      customersArray = selectedCustomers.result;
    } else if (Array.isArray(selectedCustomers)) {
      customersArray = selectedCustomers;
    }

    if (!customersArray || customersArray.length === 0) {
      MySwal.fire({
        text: "Please select customers to add",
        imageUrl: errorImage,
        customClass: "swal-error"
      });
      return;
    }

    const existingCustomerCodes = customers.map(c => c.CustomerCode).filter(Boolean);
    const duplicates = customersArray.filter(c => existingCustomerCodes.includes(c.CustomerCode));

    if (duplicates.length > 0) {
      MySwal.fire({
        text: "Customer already present in Comparison Sheet",
        imageUrl: errorImage,
        customClass: "swal-error"
      });
      return;
    }

    setLoading(true);
    try {
      const allCustomersData = [];
      
      for (const customer of customersArray) {
        try {
          const customerUsers = await getCustomerUsersByCode(customer.CustomerCode);
          
          if (customerUsers.length === 0) {
            MySwal.fire({
              text: `No active users found for customer ${customer.CustomerName}`,
              imageUrl: errorImage,
              customClass: "swal-error"
            });
            continue;
          }

          customerUsers.forEach(user => {
            allCustomersData.push({
              CustomerCode: customer.CustomerCode,
              CustomerEmail: user.email,
              CustomerUserName: user.userName,
              LinkedBPName: customer.CustomerName
            });
          });
        } catch (error) {
          console.error(`Error fetching users for customer ${customer.CustomerCode}:`, error);
        }
      }

      if (allCustomersData.length === 0) {
        MySwal.fire({
          text: "No users found for selected customers",
          imageUrl: errorImage,
          customClass: "swal-error"
        });
        setLoading(false);
        return;
      }

      const result = await addCustomerToEnquiry({
        EnquiryNumber: enquiry.EnquiryNumber,
        CustomersData: allCustomersData,
        ModifiedBy: context.userId,
        ModifiedByName: context.userName
      });

      if (result.status) {
        MySwal.fire({
          text: "Customers added successfully",
          imageUrl: successImage,
          customClass: "swal-success"
        });
        setShowAddCustomerModal(false);
        fetchComparisonData();
        } else {
        MySwal.fire({
          text: result.message || "Unable to Process Request",
          imageUrl: errorImage,
          customClass: "swal-error"
        });
      }
    } catch (error) {
      console.error("Error adding customers:", error);
      MySwal.fire({
        text: error.message || "Unable to Process Request",
        imageUrl: errorImage,
        customClass: "swal-error"
      });
    }
    setLoading(false);
  };

  const handleShowHistory = (customer = null) => {
    setSelectedCustomerHistory(customer);
    console.log("setSelectedCustomerHistory", customer);
    console.log("historyData", customers);

    let historyData = [];

    if (customer) {
      // Show history for specific customer
      if (customer.BidHistory && customer.BidHistory.length > 0) {
        historyData = customer.BidHistory.map(bid => ({
          BPCode: customer.CustomerCode || customer.BPCode,
          BPName: customer.CustomerName || customer.LinkedBPName || customer.CustomerUserName || customer.BPName,
          UserIdName: customer.CustomerUserName,
          EmailId: customer.CustomerEmail,
          BidSubmissionDateTime: formatBidDateTime(bid.BidDate),
          Price: bid.BidAmount || "No Bid Placed"
        }));
      } else {
        // If no bid history, show current bid
        historyData = [{
          BPCode: customer.CustomerCode || customer.BPCode,
          BPName: customer.CustomerName || customer.LinkedBPName || customer.CustomerUserName || customer.BPName,
          UserIdName: customer.CustomerUserName,
          EmailId: customer.CustomerEmail,
          BidSubmissionDateTime: formatBidDateTime(customer.CurrentBidDate),
          Price: customer.CurrentBid || "No Bid Placed"
        }];
      }
    } else {
      // Show global history for all customers
      customers.forEach(c => {
        if (c.BidHistory && c.BidHistory.length > 0) {
          c.BidHistory.forEach(bid => {
            historyData.push({
              BPCode: c.CustomerCode || c.BPCode,
              BPName: c.CustomerName || c.LinkedBPName || c.CustomerUserName || c.BPName,
              UserIdName: c.CustomerUserName,
              EmailId: c.CustomerEmail,
              BidSubmissionDateTime: formatBidDateTime(bid.BidDate),
              Price: bid.BidAmount || "No Bid Placed"
            });
          });
        } else {
          // If no bid history, show current bid
          historyData.push({
            BPCode: c.CustomerCode || c.BPCode,
            BPName: c.CustomerName || c.LinkedBPName || c.CustomerUserName || c.BPName,
            UserIdName: c.CustomerUserName,
            EmailId: c.CustomerEmail,
            BidSubmissionDateTime: formatBidDateTime(c.CurrentBidDate),
            Price: c.CurrentBid || "No Bid Placed"
          });
        }
      });
    }

    setHistoryRowData(historyData);
    setShowBidHistory(true);
  };

  const getHighestBidder = () => {
    if (customers.length === 0) {
      console.log("No customers found");
      return null;
    }

    const validBids = customers.filter(c => {
      // Handle both string and number types for CurrentBid
      const bidValue = c.CurrentBid;

      // Skip if withdrawn
      if (c.WithdrawStatus === "Withdrawn") {
        return false;
      }

      // Skip if Regret or No Bid Placed
      if (bidValue === "Regret" || bidValue === "No Bid Placed" || bidValue === null || bidValue === undefined || bidValue === "") {
        return false;
      }

      // Check if it's a valid number (either number type or string that can be parsed)
      const bid = typeof bidValue === 'number' ? bidValue : parseFloat(bidValue);
      const isValid = !isNaN(bid) && bid > 0;

      return isValid;
    });

    if (validBids.length === 0) {
      return null;
    }

    // Sort by bid amount (highest first) and return the first one
    const sortedValidBids = validBids.sort((a, b) => {
      const bidA = typeof a.CurrentBid === 'number' ? a.CurrentBid : parseFloat(a.CurrentBid);
      const bidB = typeof b.CurrentBid === 'number' ? b.CurrentBid : parseFloat(b.CurrentBid);
      return bidB - bidA;
    });

    return sortedValidBids[0];
  };

  if (!enquiryData) {
    return null;
  }

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" backdrop="static" className="comparison-sheet-modal">
        <Modal.Header closeButton style={{ gap: '2rem' }}>
          <Modal.Title style={{ flexGrow: '1' }}>Comparison Sheet - {enquiry?.EnquiryNumber}</Modal.Title>
          <img
            className="action-icon"
            src={download}
            alt="pdf-download"
            title="Download PDF"
            style={{ width: '2.5rem', height: '2.5rem' }}
            onClick={() => handlePdfDownload()} />
        </Modal.Header>
        <Modal.Body>
          {loading && <div className="spinner-box"> <Spinner animation="border" className="spinner" /></div>}

          {/* Global History and Controls */}
          <div className="mb-3 mt-1">
            <Button
              variant="success"
              className="btn-add-color semiBoldToggleButton rightAlignedButton"
              onClick={() => handleShowHistory()}
              style={{ width: '150px', height: '35px' }}
            >
              Global History
            </Button>
          </div>

          {/* Table 1 - Enquiry Details */}
          <div className="mb-4 mt-5">
            <h5>Enquiry Details</h5>
            <Table bordered size="sm">
              <tbody>
                <tr>
                  <td><strong>Enquiry Number</strong></td>
                  <td>{enquiryData.EnquiryNumber}</td>
                  <td><strong>Enquiry Date</strong></td>
                  <td>{formatDate(enquiryData.InsertedOn)}</td>
                  <td><strong>Due Date</strong></td>
                  <td>{formatDateInDDMMYYYY(enquiryData.DueDate)}</td>
                </tr>
                <tr>
                  <td><strong>Item Code</strong></td>
                  <td>{enquiryData.ItemCode}</td>
                  <td><strong>Item Description</strong></td>
                  <td>{enquiryData.ItemDescription}</td>
                  <td><strong>Item Details</strong></td>
                  <td>{enquiryData.ItemDetail}</td>
                </tr>
                <tr>
                  <td><strong>Item Type</strong></td>
                  <td>{enquiryData.ItemType}</td>
                  <td><strong>Quantity</strong></td>
                  <td>{enquiryData.Quantity}</td>
                  <td><strong>UOM</strong></td>
                  <td>{enquiryData.UOM}</td>
                </tr>
                <tr>
                  <td><strong>GST%</strong></td>
                  <td>{enquiryData.GST}</td>
                  <td><strong>Product Class</strong></td>
                  <td>{enquiryData.EnquirySource === "Disposal" ? enquiryData.ProductClass || "-" : "-"}</td>
                  <td><strong>Commodity Code</strong></td>
                  <td>{enquiryData.EnquirySource === "Disposal" ? enquiryData.CommodityCodes || "-" : "-"}</td>
                </tr>
                <tr>
                  <td><strong>Location</strong></td>
                  <td>{enquiryData.Location}</td>
                  <td><strong>Division</strong></td>
                  <td>{enquiryData.Division}</td>
                  <td><strong>Disposal Intimation Number</strong></td>
                  <td>{enquiryData.EnquirySource === "Disposal" ? enquiryData.EnquirySourceNumber || "-" : "-"}</td>
                </tr>
                <tr>
                  <td><strong>Logistic Company</strong></td>
                  <td>{enquiryData.LogisticCompany}</td>
                  <td><strong>SO Number</strong></td>
                  <td>{enquiryData.SONumber || "-"}</td>
                  <td><strong>Last Rate</strong></td>
                  <td>{enquiryData.LastRate || "-"}</td>
                </tr>
              </tbody>
            </Table>
          </div>

          {/* Table 2 - Comparison Sheet */}
          <div className="mb-2 comparisonTable">
            <h5>Comparison Sheet</h5>
            {loading ? (
              <div className="spinner-box"> <Spinner animation="border" className="spinner" /></div>
            ) : comparisonRowData.length > 0 ? (
              <Table bordered size="sm" responsive>
                <thead style={{ backgroundColor: '#F1F1ED' }}>
                  <tr>
                    <th style={{ backgroundColor: '#F1F1ED', fontWeight: 'bold' }}>Select for Finalize</th>
                    <th style={{ backgroundColor: '#F1F1ED', fontWeight: 'bold' }}>Select for Due Date Extension</th>
                    <th style={{ backgroundColor: '#F1F1ED', fontWeight: 'bold' }}>BP Code</th>
                    <th style={{ backgroundColor: '#F1F1ED', fontWeight: 'bold' }}>BP Name</th>
                    <th style={{ backgroundColor: '#F1F1ED', fontWeight: 'bold' }}>Price ₹ (Excl. taxes)</th>
                    <th style={{ backgroundColor: '#F1F1ED', fontWeight: 'bold' }}>Bid Submission Date Time</th>
                    <th style={{ backgroundColor: '#F1F1ED', fontWeight: 'bold' }}>Revised Due Date</th>
                    <th style={{ backgroundColor: '#F1F1ED', fontWeight: 'bold' }}>History</th>
                    <th style={{ backgroundColor: '#F1F1ED', fontWeight: 'bold' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRowData.map((customer, index) => {
                    const customerId = customer.CustomerCode;

                    // Calculate highest bid value and all highest bidders
                    const validBids = comparisonRowData.filter(c => {
                      const bidValue = c.CurrentBid;
                      if (c.WithdrawStatus === "Withdrawn") return false;
                      if (bidValue === "Regret" || bidValue === "No Bid Placed" || bidValue === null || bidValue === undefined || bidValue === "") return false;
                      const bid = typeof bidValue === 'number' ? bidValue : parseFloat(bidValue);
                      return !isNaN(bid) && bid > 0;
                    });

                    const highestBidValue = validBids.length > 0 ? Math.max(...validBids.map(c => {
                      const bidValue = typeof c.CurrentBid === 'number' ? c.CurrentBid : parseFloat(c.CurrentBid);
                      return bidValue;
                    })) : null;

                    const customerBidValue = typeof customer.CurrentBid === 'number' ? customer.CurrentBid : parseFloat(customer.CurrentBid);
                    const isHighestBidder = highestBidValue !== null && !isNaN(customerBidValue) && customerBidValue === highestBidValue && customer.WithdrawStatus !== "Withdrawn";
                    
                    const hasValidBid = customer.CurrentBid &&
                      customer.CurrentBid !== "Regret" &&
                      customer.CurrentBid !== "No Bid Placed" &&
                      !isNaN(parseFloat(customer.CurrentBid));

                    const rowClassName = isHighestBidder && hasValidBid ? 'highest-bidder-row' :
                      selectedForFinalize[customerId]?.checked ? 'selected-finalize-row' : '';

                    return (
                      <tr key={customerId} className={rowClassName}>
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={selectedForFinalize[customerId]?.checked || false}
                            disabled={selectedForFinalize[customerId]?.disabled !== false}
                            onChange={(e) => {
                              setSelectedForFinalize(prev => ({
                                ...prev,
                                [customerId]: {
                                  checked: e.target.checked,
                                  disabled: selectedForFinalize[customerId]?.disabled !== false
                                }
                              }));
                            }}
                          />
                        </td>
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={selectedForDueDate[customerId]?.checked || false}
                            disabled={selectedForDueDate[customerId]?.disabled || customer.WithdrawStatus === "Withdrawn"}
                            onChange={(e) => {
                              setSelectedForDueDate(prev => ({
                                ...prev,
                                [customerId]: {
                                  checked: e.target.checked,
                                  disabled: selectedForDueDate[customerId]?.disabled || false
                                }
                              }));
                            }}
                          />
                        </td>
                        <td>{customer.CustomerCode}</td>
                        <td>{customer.CustomerName}</td>
                        <td>{customer.CurrentBid || "No Bid Placed"}</td>
                        <td>{formatBidDateTime(customer.CurrentBidDate)}</td>
                        <td>{customer.RevisedDueDate || "-"}</td>
                        <td>
                          <Button
                            variant="outline-primary"
                            className="swal2-styled.swal2-deny"
                            size="sm"
                            onClick={() => handleShowHistory(customer)}
                          >
                            History
                          </Button>
                        </td>
                        <td>
                          {isHighestBidder && hasValidBid && customer.WithdrawStatus !== "Withdrawn" ? (
                            <Button
                              variant="outline-primary"
                              className="swal2-styled.swal2-deny"
                              size="sm"
                              onClick={() => {
                                setSelectedWithdrawCustomer(customer);
                                setShowWithdrawModal(true);
                                setWithdrawReason("");
                              }}
                            >
                              Withdraw
                            </Button>
                          ) : customer.WithdrawStatus === "Withdrawn" ? (
                            <span className="text-danger">
                              Withdrawn: {customer.WithdrawReason}
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : (
              <div className="text-center p-3">
                {enquiryData ? "No Customer Present For this Enquiry" : "Unable to load comparison data"}
              </div>
            )}

            <Row className="mt-3">
              <Col md={6} className="d-flex align-items-end">
                <Button
                  variant="success"
                  onClick={handleReviseDate}
                  disabled={loading}
                  className="btn-add-color semiBoldToggleButton"
                  style={{ width: '150px', height: '35px', marginBottom: '10px' }}
                >
                  Revise Due Date
                </Button>
              </Col>
              <Col md={6}>
                <Button
                  variant="success"
                  onClick={handleAddCustomer}
                  disabled={loading}
                  className="btn-add-color semiBoldToggleButton rightAlignedButton"
                  style={{ width: '150px', height: '35px', marginBottom: '10px' }}
                >
                  Add Customer
                </Button>
              </Col>
            </Row>
          </div>

          <div className="mb-1">
            <Form.Group className="g-input">
              <Form.Control
                as="textarea"
                rows={3}
                value={enquiryRemarks}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 250 && /^[a-zA-Z0-9 \-.,()]*$/.test(value)) {
                    setEnquiryRemarks(value);
                  }
                }}
                maxLength={250}
                placeholder=" "
              />
              <Form.Label>Enquiry Remarks</Form.Label>
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ justifyContent: 'space-between' }}>
          <Col md={6}>
            <Button variant="outline-primary" onClick={handleRejectEnquiry} disabled={loading} className="semiBoldToggleButton cancelBtn">
              Reject
            </Button>
          </Col>
          <Col md={3}>
            <Button variant="success" onClick={handleFreezeEnquiry} disabled={loading} className="btn-add-color semiBoldToggleButton rightAlignedButton">
              Freeze
            </Button>
          </Col>
        </Modal.Footer>
      </Modal>

      {/* Revise Due Date Modal */}
      {showReviseModal &&
        <ReviseDueDateModal
          show={showReviseModal}
          onHide={() => setShowReviseModal(false)}
          revisedDueDate={revisedDueDate}
          setRevisedDueDate={setRevisedDueDate}
          onSave={handleSaveRevisedDate}
          loading={loading}
        />
      }

      {/* Add Customer Modal */}
      {showAddCustomerModal && (
        <MultiValuePopUp
          show={showAddCustomerModal}
          onHide={() => setShowAddCustomerModal(false)}
          setSelected={handleAddSelectedCustomers}
          masterName="Customer Master"
          selectedItems={""}
        />
      )}

      {/* Bid History Modal */}
      {showBidHistory &&
        <BidHistoryModal
          show={showBidHistory}
          onHide={() => setShowBidHistory(false)}
          selectedCustomerHistory={selectedCustomerHistory}
          historyRowData={historyRowData}
          historyColumns={historyColumns}
        />
      }

      {/* PDF Modal */}
      {showPDFModal && (
        <ComparisonSheetPDFModal
          show={showPDFModal}
          onHide={() => setShowPDFModal(false)}
          enquiryData={enquiryData}
          comparisonRowData={comparisonRowData}
          // lnCompany={lnCompany}
          enquiryRemarks={enquiryRemarks}
        />
      )}

      {/* Withdraw Modal */}
      {showWithdrawModal &&
        <WithdrawBidderModal
          show={showWithdrawModal}
          onHide={() => setShowWithdrawModal(false)}
          withdrawReason={withdrawReason}
          setWithdrawReason={setWithdrawReason}
          onSubmit={handleWithdrawBidder}
          loading={loading}
          error={error}
        />
      }
    </>
  );
};

export default ComparisonSheet;
